import { LegalDoc } from '../legal-doc.model';

/**
 * Authoritative German version. The en/fr/it files mirror this structure
 * section for section — keep the `num` values and their order in sync.
 */
export const CHRONIQ_PRIVACY_DE: LegalDoc = {
    title: 'Datenschutzerklärung Chroniq',
    meta: 'Stand: 3. November 2026 · Gültig für die Chroniq-App (iOS, Web) und chroniq.ch',
    tocHeading: 'Inhalt',
    footer: 'Massgeblich ist die deutsche Fassung. Übersetzungen dienen der Verständlichkeit.',
    sections: [
        {
            num: '01',
            title: 'Rollenverteilung: Verantwortliche und Auftragsbearbeiterin',
            body: `
        <p>Chroniq ist eine mandantenfähige Software-as-a-Service-Lösung, die Betrieben («Kunde» oder
        «Arbeitgeber») zur Zeiterfassung und Personalverwaltung zur Verfügung gestellt wird.</p>
        <p>Bei der Bearbeitung von Mitarbeitendendaten — Zeiterfassung, Schichtplanung, Abwesenheiten,
        Chat, Mitarbeitendenprofil — ist nach dem Schweizer Datenschutzgesetz (DSG) und, soweit anwendbar,
        der Datenschutz-Grundverordnung (DSGVO) <strong>der jeweilige Arbeitgeber die verantwortliche
        Stelle</strong>. Die <strong>Gastro Management Oberbillig</strong> («Betreiberin», «wir»)
        handelt insoweit als <strong>Auftragsbearbeiterin</strong> nach Art. 9 DSG bzw. Art. 28 DSGVO,
        auf Grundlage eines mit jedem Kunden geschlossenen Auftragsbearbeitungsvertrags.</p>
        <p><strong>Selbst verantwortlich</strong> sind wir für den Betrieb, die Sicherheit und die
        Weiterentwicklung der Plattform einschliesslich technischer Diagnosedaten (Ziff. 09), für die
        Verwaltung der Kundenbeziehung sowie für unsere Website.</p>
        <p>Diese Erklärung informiert umfassend über die Bearbeitung innerhalb von Chroniq. Arbeitgeber
        können sie zugleich als Grundlage für ihre eigene Informationspflicht gegenüber Mitarbeitenden
        verwenden (Art. 328b OR, Art. 19 DSG, Art. 13 f. DSGVO). Mitarbeitende richten Fragen zu einer
        konkreten Bearbeitung bitte zuerst an ihren Arbeitgeber; für Fragen zur Plattform stehen wir
        ebenfalls zur Verfügung:</p>
        <p><strong>Gastro Management Oberbillig</strong><br>
        Einzelunternehmen, Inhaber: Erik Alexander Oberbillig<br>
        Betreiberin der Chroniq-App<br>
        Kirchenrütti 14, 6463 Bürglen UR, Schweiz</p>
        <p>Datenschutz Chroniq: <a href="mailto:datenschutz@chroniq.ch">datenschutz&#64;chroniq.ch</a><br>
        Zentrale Datenschutzstelle der Gruppe: <a href="mailto:privacy@gastro-management.ch">privacy&#64;gastro-management.ch</a></p>
      `,
        },
        {
            num: '02',
            title: 'Anmeldedaten',
            body: `
        <p>Die Authentifizierung erfolgt über <strong>Auth0</strong> auf einem EU-Mandanten
        (<code>*.eu.auth0.com</code>) mit Datenhaltung in der EU. Anbieterin ist Okta, Inc. Bearbeitet
        werden:</p>
        <ul>
          <li>E-Mail-Adresse</li>
          <li>Vor- und Nachname</li>
          <li>Profilbild (optional, aus dem Authentifizierungstoken)</li>
          <li>Benutzerkennung (Auth0 Subject Identifier)</li>
          <li>Zugriffsrolle (<em>employee</em>, <em>chef</em>, <em>admin</em>)</li>
          <li>Onboarding-Status</li>
        </ul>
        <p>Auth0 ist als Auftragsbearbeiterin eingebunden; es besteht ein Auftragsbearbeitungsvertrag mit
        Okta, Inc. Da die Konzernmutter dem US-Recht untersteht und ein Zugriff aus den USA im Rahmen von
        Support und Wartung nicht vollständig ausgeschlossen werden kann, ist dieser mögliche
        Drittlandbezug über die EU-Standardvertragsklauseln abgesichert (Ziff. 13).</p>
        <p><strong>Passwörter speichern wir nicht.</strong></p>
      `,
        },
        {
            num: '03',
            title: 'Zeiterfassungsdaten',
            body: `
        <p>Über die App werden erfasst:</p>
        <ul>
          <li>Beginn, Ende und Datum von Arbeitseinsätzen</li>
          <li>Pausen (Beginn, Ende, Dauer)</li>
          <li>Arbeitsstatus (laufend, pausiert, abgeschlossen)</li>
          <li>optionale Notizen zum Einsatz</li>
          <li>tägliche und wöchentliche Arbeitszeitkennzahlen sowie der Überstundensaldo</li>
        </ul>
        <p>Dazu gehören Zeitkorrekturen und Nachträge samt Begründung und Entscheid der Vorgesetzten sowie der
        Monatsabschluss. Ein <strong>Änderungsprotokoll</strong> hält bei schreibenden Änderungen an Personen- und
        Zeitdaten fest, wer wann was geändert hat (Benutzerkennung und Rolle der handelnden Person, Zeitpunkt,
        betroffener Datensatz, geänderte Felder). Sensible Felder wie AHV-Nummer oder IBAN werden darin nicht im
        Klartext abgelegt. Das Protokoll dient der Nachvollziehbarkeit gegenüber dem Arbeitgeber und der
        Sicherheit und wird nicht zur Verhaltens- oder Leistungskontrolle ausgewertet (Ziff. 19).</p>
        <p>Diese Bearbeitung dient der Erfüllung der Aufzeichnungspflicht des Arbeitgebers nach Art. 46 des
        Arbeitsgesetzes und Art. 73 der Verordnung 1 zum Arbeitsgesetz sowie der korrekten Lohnabrechnung.</p>
        <p><strong>Keine Standorterfassung:</strong> Chroniq erhebt beim Stempeln <strong>keine</strong>
        Standort- oder Positionsdaten des Endgeräts und wertet keine Bewegungsprofile aus.</p>
      `,
        },
        {
            num: '04',
            title: 'Zeiterfassung über Badge-Terminals',
            body: `
        <p>Setzt ein Arbeitgeber physische RFID-Badge-Terminals ein, werden Scan-Ereignisse (Badge-Kennung,
        Gerätekennung, Zeitpunkt) über Microsoft Azure IoT Hub bearbeitet, einer mitarbeitenden Person
        zugeordnet und als Kommen-/Gehen-Buchung übernommen; an das Terminal geht eine kurze Rückmeldung. Am Terminal können auch Pausen gescannt werden (Pause, Pause beenden).</p>
        <p>Die Badge-Kennung ist eine reine Kartennummer und enthält keine biometrischen Merkmale.</p>
        <p>Nach dem Scan zeigt das Terminal kurz den Namen, das Ergebnis und, sofern der Betrieb dies nicht
        abgeschaltet hat, den Tagesstand („Heute gearbeitet“ und „Pausen“). Damit das Terminal auch ohne
        Netzverbindung arbeitet, speichert es die Namen der Badge-Inhaber und noch nicht gesendete Scans
        verschlüsselt lokal auf dem Gerät. Der Betreiber kann zu Wartungszwecken per Fernzugriff auf das
        Gerät zugreifen. Die Aufbewahrung der Zeiterfassungsdaten richtet sich nach Ziff. 11.</p>
        <p>Anstelle einer physischen Karte kann auch ein digitaler Badge in Apple Wallet verwendet werden
        (Ziff. 08).</p>
        <p><em>Diese Funktion befindet sich im Pilotbetrieb</em> und ist nur bei einzelnen Betrieben
        aktiviert, die das Gateway-Terminal ausdrücklich einsetzen.</p>
      `,
        },
        {
            num: '05',
            title: 'Schichtplanung und Abwesenheiten',
            body: `
        <p>Vorgesetzte erstellen Schichten und entscheiden über Abwesenheitsgesuche. Dabei werden
        bearbeitet:</p>
        <ul>
          <li>Schichtzeiten, Schichtvorlagen und Kalenderexporte (iCal)</li>
          <li>Abwesenheitsart (<em>Ferien</em>, <em>Krankheit</em>, <em>Unfall</em>,
              <em>Mutterschaft/Vaterschaft</em>, <em>Militär</em>, <em>Weiterbildung</em>,
              <em>Kompensationstag</em>, <em>Sonstiges</em>) als blosse Kategorie ohne Diagnose- oder
              Attestfeld, Zeitraum, Status und Ferienkonto</li>
          <li>Schichttausch-Anfragen zwischen Mitarbeitenden und deren Entscheid durch Vorgesetzte</li>
          <li>die Freitextfelder «Notiz» und «Ablehnungsgrund»</li>
        </ul>
        <p><strong>Hinweis zur Datenminimierung:</strong> Die Kategorien «Krankheit», «Unfall» und «Mutterschaft/Vaterschaft» erfassen technisch
        keine Diagnose. Die Freitextfelder sind jedoch ungeprüfter Klartext. Mitarbeitende und Vorgesetzte werden
        gebeten, dort keine Gesundheitsangaben und keine anderen besonders schützenswerten Personendaten
        (Art. 5 lit. c DSG / Art. 9 DSGVO) einzutragen, soweit dies nicht ausdrücklich erforderlich ist.
        Werden solche Daten dennoch eingegeben, bearbeiten wir sie im Auftrag des Arbeitgebers mit
        denselben Zugriffsbeschränkungen wie die übrigen Personaldaten.</p>
      `,
        },
        {
            num: '06',
            title: 'Mitarbeitendendaten und Profil',
            body: `
        <p>Im System werden folgende Personaldaten geführt:</p>
        <ul>
          <li>Name, E-Mail-Adresse, Telefonnummer</li>
          <li>Personalnummer, Stellenbezeichnung, Abteilung, Eintrittsdatum</li>
          <li>Einladungsstatus (ausstehend, angenommen, widerrufen)</li>
          <li>optionales Profilbild in einem privaten Speicher-Container, abrufbar ausschliesslich über
              zeitlich befristete, signierte Adressen</li>
        </ul>
        <p>Ausschliesslich Personen mit der Rolle <em>chef</em> oder <em>admin</em> können zusätzlich
        bearbeiten:</p>
        <ul>
          <li>AHV-Nummer</li>
          <li>IBAN beziehungsweise Bankverbindung</li>
          <li>Geburtsdatum</li>
          <li>Wohnadresse</li>
          <li>Notfallkontakt (Name, Beziehung, Telefonnummer)</li>
        </ul>
        <p>Nach Art. 328b OR darf der Arbeitgeber nur Daten bearbeiten, die die Eignung für das
        Arbeitsverhältnis betreffen oder zur Durchführung des Arbeitsvertrags erforderlich sind. Der
        Arbeitgeber ist dafür verantwortlich, nur solche Felder zu befüllen.</p>
      `,
        },
        {
            num: '07',
            title: 'Team-Chat und Ankündigungen',
            body: `
        <p>Ist das Chat-Feature aktiviert, werden bearbeitet:</p>
        <ul>
          <li>Nachrichteninhalt, Absender, Zeitpunkt sowie Bearbeitungs- und Löschstatus. Gelöschte
              Nachrichten werden aus der Anzeige entfernt; der Inhalt bleibt technisch in der Datenhaltung
              bestehen (Soft-Delete) und wird mit der Anonymisierung des Kontos oder auf Weisung des
              Arbeitgebers endgültig entfernt.</li>
          <li>Lesebestätigungen je Nutzerin oder Nutzer und Nachricht</li>
          <li>Dateianhänge (PDF, JPEG, PNG, höchstens 20 MB) in einem privaten Speicher-Container; der
              Abruf erfolgt ausschliesslich über signierte Adressen mit einer Gültigkeit von einer Stunde</li>
          <li>Ankündigungen je Betrieb: von Vorgesetzten oder Administrierenden verfasste Mitteilungen, die
              alle Mitarbeitenden des Betriebs lesen können</li>
        </ul>
        <p>Der Chat dient der betrieblichen Kommunikation. Eine systematische Auswertung der
        Kommunikationsinhalte zur Verhaltens- oder Leistungskontrolle findet nicht statt und ist dem
        Arbeitgeber nach Ziff. 19 untersagt.</p>
      `,
        },
        {
            num: '08',
            title: 'Benachrichtigungen und Apple Wallet',
            body: `
        <p>Bei Schichtänderungen, Schichttausch-Anfragen, Zeitkorrekturen und Entscheiden über
        Abwesenheitsgesuche wird auf iOS-Geräten eine Push-Benachrichtigung über den <strong>Apple Push Notification Service</strong> ausgelöst. Dazu
        speichern wir je Gerät ein Push-Token zusammen mit einer Gerätekennung; beim Abmelden wird beides
        gelöscht. Mitarbeitende können Benachrichtigungen in den Profileinstellungen und in den
        Systemeinstellungen des Geräts abbestellen. In der Webanwendung erfolgt keine Push-Zustellung;
        dort erscheinen Hinweise nur innerhalb der geöffneten Anwendung.</p>
        <p>Hat der Arbeitgeber die Überstunden-Benachrichtigung aktiviert, erhalten Mitarbeitende zu Beginn jedes
        Monats eine E-Mail mit ihrem Überstundensaldo. Der Versand erfolgt über Azure Communication Services
        (Ziff. 12).</p>
        <p>Ist das Wallet-Feature aktiviert, erzeugt das Backend ein signiertes <em>.pkpass</em>-Dokument
        als Mitarbeitendenausweis. Es wird lokal auf dem Gerät gespeichert; im System verbleibt lediglich
        der Registrierungsstatus des Geräts für automatische Aktualisierungen.</p>
      `,
        },
        {
            num: '09',
            title: 'Technische Verbindungs- und Diagnosedaten',
            body: `
        <ul>
          <li>Echtzeit-Verbindungsdaten über den <strong>Azure SignalR Service</strong> für Live-Aktualisierungen</li>
          <li>Metadaten der Anfragen (IP-Adresse, aufgerufene Adresse, Zeitstempel, Statuscode) sowie
              Fehlerprotokolle, bearbeitet durch <strong>Azure Application Insights</strong> zu Betriebs-,
              Diagnose- und Sicherheitszwecken</li>
        </ul>
        <p>Diese Bearbeitung erfolgt auf Azure-Infrastruktur in der EU. Für diese Daten sind wir selbst
        verantwortlich (Ziff. 01).</p>
      `,
        },
        {
            num: '10',
            title: 'Zwecke und Rechtsgrundlagen',
            body: `
        <ul>
          <li><strong>Vertragserfüllung</strong> (Art. 31 Abs. 2 lit. a DSG / Art. 6 Abs. 1 lit. b DSGVO):
              Erbringung der Zeiterfassungs- und Personalverwaltungsleistung gegenüber dem Arbeitgeber und
              Durchführung des Arbeitsvertrags.</li>
          <li><strong>Gesetzliche Pflicht</strong> (Art. 6 Abs. 1 lit. c DSGVO): Erfüllung der
              arbeitsrechtlichen Aufzeichnungs- und Aufbewahrungspflichten nach Art. 46 ArG, Art. 73 ArGV 1
              sowie der buchhalterischen Pflichten nach OR.</li>
          <li><strong>Berechtigtes Interesse</strong> (Art. 31 Abs. 1 DSG / Art. 6 Abs. 1 lit. f DSGVO):
              Betrieb, Sicherheit und Missbrauchsabwehr der Plattform einschliesslich technischer
              Diagnosedaten (Ziff. 09).</li>
          <li><strong>Einwilligung</strong> (Art. 6 Abs. 6 DSG / Art. 6 Abs. 1 lit. a DSGVO): rein optionale
              Funktionen wie Profilbild, Push-Benachrichtigungen und Apple Wallet, die Mitarbeitende selbst
              aktivieren. Eine Einwilligung im Arbeitsverhältnis ist nur wirksam, wenn sie freiwillig
              erteilt wird; ihre Verweigerung hat für Mitarbeitende keine Nachteile und kann jederzeit
              widerrufen werden.</li>
        </ul>
      `,
        },
        {
            num: '11',
            title: 'Aufbewahrung und Löschung',
            body: `
        <p>Die Daten liegen ausschliesslich auf <strong>Microsoft-Azure</strong>-Infrastruktur in der EU
        (primär Westeuropa).</p>
        <ul>
          <li><strong>Zeiterfassungs- und Schichtdaten:</strong> mindestens 5 Jahre nach Ablauf ihrer
              Gültigkeit (Art. 73 Abs. 2 ArGV 1); danach Löschung oder Anonymisierung auf Weisung des
              Arbeitgebers oder bei Vertragsende. Eine automatische Löschung nach Fristablauf ist
              derzeit nicht eingerichtet.</li>
          <li><strong>Änderungsprotokoll (Ziff. 03):</strong> nach Aufbewahrungskategorie gekennzeichnet
              (Sitzungsdaten 90 Tage, Betriebsdaten 2 Jahre, rechtlich relevante Daten 10 Jahre). Eine
              automatische Löschung ist derzeit nicht eingerichtet.</li>
          <li><strong>Belege mit buchhalterischer Relevanz:</strong> 10 Jahre (Art. 958f OR).</li>
          <li><strong>Personaldaten:</strong> bis zum Ende des Arbeitsverhältnisses und anschliessend nur
              so lange, wie eine gesetzliche Pflicht oder ein durchsetzbarer Anspruch dies erfordert.</li>
          <li><strong>Chat- und Ankündigungsnachrichten:</strong> bis zur Löschung durch berechtigte
              Personen oder bis zur Anonymisierung des zugehörigen Kontos.</li>
          <li><strong>Technische Diagnosedaten (Ziff. 09):</strong> nach der Standardaufbewahrung von Azure
              Application Insights, in der Regel bis zu 90 Tage.</li>
        </ul>
        <p>Der Arbeitgeber kann für die von ihm verantworteten Daten kürzere Fristen anweisen. Nach Ende
        des Kundenvertrags löschen oder retournieren wir die Daten nach seiner Weisung.</p>
      `,
        },
        {
            num: '12',
            title: 'Empfänger und Auftragsbearbeiter',
            body: `
        <p>Eine Weitergabe erfolgt nur an Auftragsbearbeiter, die vertraglich zur Einhaltung des
        Datenschutzes verpflichtet sind:</p>
        <ul>
          <li><strong>Okta, Inc. (Auth0)</strong> — Authentifizierung, EU-Mandant mit Datenhaltung in der
              EU; US-Konzernmutter, abgesichert über Standardvertragsklauseln (Ziff. 02, 13)</li>
          <li><strong>Microsoft Ireland Operations Limited (Azure)</strong> — Cloud-Infrastruktur,
              Datenbank und Speicher, SignalR, Application Insights, Versand transaktionaler E-Mails
              (Azure Communication Services, insbesondere Einladungen an Mitarbeitende) sowie, sofern
              aktiviert, IoT Hub für Badge-Terminals; EU-Rechenzentren, primär Westeuropa</li>
          <li><strong>Apple Inc.</strong> — Zustellung von Push-Benachrichtigungen und, bei aktiviertem
              Wallet-Feature, Bereitstellung des Ausweises auf dem Gerät</li>
          <li><strong>Cloudflare, Inc.</strong> — Namensauflösung (DNS) sowie Weiterleitung und Schutz des Datenverkehrs
              zu chroniq.ch; dabei werden Anfragedaten (insbesondere IP-Adresse) über das weltweite Netz von
              Cloudflare geleitet, auch ausserhalb der EU; abgesichert über Standardvertragsklauseln (Ziff. 13)</li>
        </ul>
        <p>Eine Weitergabe an weitere Dritte, eine Nutzung zu Werbezwecken, ein Verkauf von Personendaten
        oder eine Verwendung zum Training von KI-Modellen findet nicht statt. Eine vollständige Liste der
        Unterauftragsbearbeiter stellen wir auf Anfrage bereit; neue Unterauftragsbearbeiter kündigen wir
        den Kunden vorgängig an.</p>
      `,
        },
        {
            num: '13',
            title: 'Bearbeitung im Ausland',
            body: `
        <p>Die Datenhaltung erfolgt in der EU. Einzelne Anbieter unterstehen als US-Gesellschaften
        US-Recht; ein Zugriff aus den USA im Rahmen von Support und Wartung lässt sich nicht vollständig
        ausschliessen.</p>
        <p>Solche Übermittlungen stützen sich auf die <strong>Standardvertragsklauseln</strong> der
        EU-Kommission (Art. 46 Abs. 2 lit. c DSGVO) in Verbindung mit Art. 16 Abs. 2 lit. d DSG samt den
        für die Schweiz erforderlichen Anpassungen, ergänzt um den Angemessenheitsbeschluss zum
        <strong>EU–U.S. Data Privacy Framework</strong> und die Schweizer Anerkennung des
        <strong>Swiss–U.S. Data Privacy Framework</strong>, soweit der Anbieter zertifiziert ist. Wir
        führen die Standardvertragsklauseln bewusst als eigenständige Grundlage mit, damit die
        Übermittlung auch bei einem Wegfall des Angemessenheitsbeschlusses abgesichert bleibt.</p>
        <p>Eine Kopie der Garantien stellen wir auf Anfrage zur Verfügung:
        <a href="mailto:datenschutz@chroniq.ch">datenschutz&#64;chroniq.ch</a></p>
      `,
        },
        {
            num: '14',
            title: 'Datensicherheit',
            body: `
        <ul>
          <li>Alle Übertragungen erfolgen verschlüsselt über TLS/HTTPS; ruhende Daten sind verschlüsselt.</li>
          <li>Zugriffe sind rollenbasiert gesteuert; besonders schützenswerte Felder sind ausschliesslich
              für berechtigte Rollen sichtbar, zusätzlich abgesichert durch mandantenspezifische
              Zeilenfilterung in der Datenbank.</li>
          <li>Dateianhänge und Profilbilder liegen in privaten Speicher-Containern ohne anonymen Zugriff
              und werden nur über zeitlich befristete, signierte Adressen ausgeliefert.</li>
          <li>Die Authentifizierung erfolgt ausschliesslich über Auth0 mit OAuth 2.0 / OIDC.</li>
          <li>Im Web-Portal liegen die Anmeldetoken bis zur Abmeldung im lokalen Speicher des Browsers
              (technisch notwendig, damit die Sitzung ein Neuladen übersteht); auf nativen iOS-Geräten
              liegen sicherheitskritische Werte im Schlüsselspeicher des Betriebssystems.</li>
        </ul>
        <p>Verletzungen der Datensicherheit melden wir dem EDÖB so rasch als möglich (Art. 24 DSG). Da wir
        bei Mitarbeitendendaten Auftragsbearbeiterin sind, informieren wir zudem unverzüglich den
        betroffenen Arbeitgeber, damit dieser seine Meldepflichten — nach Art. 33 DSGVO innert 72 Stunden —
        erfüllen kann.</p>
      `,
        },
        {
            num: '15',
            title: 'Cookies und lokale Speicher',
            body: `
        <p>Website und App setzen keine Marketing- oder Analyse-Cookies Dritter ein. Verwendet werden
        ausschliesslich technisch notwendige lokale Speicher:</p>
        <ul>
          <li>Darstellungspräferenz (Hell- oder Dunkelmodus)</li>
          <li>Im Web-Portal die Anmeldetoken (bis zur Abmeldung) sowie die Auswahl des Betriebs bei
              Administrierenden, damit die Sitzung ein Neuladen übersteht</li>
          <li>Zwischenspeicherung laufender Zeiterfassungen sowie eine Warteschlange für Buchungen ohne
              Netzverbindung, die bei Wiederverbindung automatisch abgeglichen wird</li>
        </ul>
        <p>Für diese technisch notwendigen Zugriffe ist keine Einwilligung erforderlich.</p>
      `,
        },
        {
            num: '16',
            title: 'Ihre Rechte',
            body: `
        <p>Als betroffene Person haben Sie das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der
        Bearbeitung, Datenherausgabe beziehungsweise -übertragung sowie auf Widerspruch gegen eine
        Bearbeitung, die auf einem berechtigten Interesse beruht (Art. 25 ff. DSG / Art. 15 ff. DSGVO).
        Eine erteilte Einwilligung können Sie jederzeit mit Wirkung für die Zukunft widerrufen.</p>
        <p>Da wir bei Personaldaten als Auftragsbearbeiterin des Arbeitgebers handeln, leiten wir Anfragen
        zu diesen Daten an den zuständigen Arbeitgeber weiter beziehungsweise bearbeiten sie in dessen
        Auftrag. <strong>Bitte richten Sie solche Anliegen zuerst an Ihren Arbeitgeber.</strong></p>
        <p>Ein Löschbegehren setzen wir technisch durch <strong>Anonymisierung</strong> des Mitarbeitendenkontos
        um: Name, Kontakt-, Bank- und weitere besonders schützenswerte Daten werden im Personalstamm
        unwiderruflich entfernt. Profilbild, Push-Registrierungen und das Anmeldekonto beim
        Authentifizierungsdienst entfernen wir auf Anweisung des Arbeitgebers separat.
        Zeiterfassungs-, Schicht- und Prüfdaten bleiben aus gesetzlichen Aufbewahrungsgründen (Art. 46 ArG,
        Art. 73 ArGV 1, OR) in anonymisierter Form bestehen. Bereits versendete Chat-Nachrichten bleiben
        unter dem zum Sendezeitpunkt gültigen Namen sichtbar, weil dieser in der Nachricht historisch
        mitgespeichert ist.</p>
        <p>Kontakt: <a href="mailto:datenschutz@chroniq.ch">datenschutz&#64;chroniq.ch</a></p>
        <p>Sie können sich zudem beim <strong>Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten
        (EDÖB)</strong>, Feldeggweg 1, 3003 Bern, beschweren:
        <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer">www.edoeb.admin.ch</a>.
        Fällt die Bearbeitung unter die DSGVO, steht Ihnen zusätzlich die Beschwerde bei der
        Aufsichtsbehörde Ihres Aufenthaltsorts oder Arbeitsplatzes zu (Art. 77 DSGVO).</p>
      `,
        },
        {
            num: '17',
            title: 'Internationale Kunden und anwendbares Recht',
            body: `
        <p>Chroniq wird in der Schweiz und international eingesetzt. Wir wenden ein gestuftes Regime an:</p>
        <ul>
          <li><strong>Schweiz:</strong> massgeblich ist das DSG als Recht unseres Sitzstaates.</li>
          <li><strong>EU/EWR:</strong> Für betroffene Personen im EU/EWR-Raum gilt die DSGVO, soweit wir
              Leistungen für dort ansässige Unternehmen erbringen oder das Verhalten von Personen in der
              EU/EWR beobachten (Art. 3 Abs. 2 DSGVO). Die Schweiz verfügt gegenüber der EU über einen
              Angemessenheitsbeschluss; Übermittlungen zwischen dem EU/EWR-Raum und unserer Schweizer
              Infrastruktur bedürfen daher keiner zusätzlichen Garantien. Soweit nach Art. 27 DSGVO
              erforderlich, benennen wir eine Vertretung in der EU; deren Kontaktangaben werden hier
              ergänzt, sobald sie benannt ist.</li>
          <li><strong>Vereinigtes Königreich:</strong> Es gilt entsprechend die UK-GDPR.</li>
          <li><strong>Weitere Länder:</strong> Wir halten die jeweils anwendbaren lokalen Vorschriften ein,
              soweit sie sich auf unsere Leistung auswirken; länderspezifische Zusatzvereinbarungen stellen
              wir auf Anfrage bereit.</li>
        </ul>
      `,
        },
        {
            num: '18',
            title: 'Künstliche Intelligenz',
            body: `
        <p>Chroniq setzt <strong>keine</strong> Systeme künstlicher Intelligenz im Sinne der Verordnung
        (EU) 2024/1689 («KI-Verordnung») ein. Schichtplanung, Überstundenberechnung, Ferienkonto und die
        Behandlung von Abwesenheitsgesuchen beruhen ausschliesslich auf fest programmierter, regelbasierter
        Logik.</p>
        <p>Die KI-Verordnung ist seit dem 2. August 2026 allgemein anwendbar. Für den Personalbereich ist
        sie besonders streng: KI-Systeme, die arbeitsbezogene Aufgaben auf Grundlage individuellen
        Verhaltens oder persönlicher Merkmale zuweisen oder die Leistung und das Verhalten von
        Mitarbeitenden überwachen und bewerten, gelten nach Anhang III Ziff. 4 als
        <strong>Hochrisiko-Systeme</strong>. Die Emotionserkennung am Arbeitsplatz ist nach
        Art. 5 Abs. 1 lit. f seit dem 2. Februar 2025 <strong>verboten</strong>. Chroniq enthält keine
        solche Funktion.</p>
        <p>Sollte künftig eine KI-Komponente eingeführt werden — etwa im Rahmen eines Auswertungs-
        oder Prognose-Features —, nehmen wir vorgängig eine Einstufung nach der KI-Verordnung vor und
        aktualisieren diese Erklärung vor der Inbetriebnahme. Bei einem Hochrisiko-System hätte zudem der
        Arbeitgeber als Betreiber die Pflichten nach Art. 26 zu erfüllen, insbesondere die Information der
        Arbeitnehmervertretung und der betroffenen Mitarbeitenden vor der Inbetriebnahme
        (Art. 26 Abs. 7).</p>
      `,
        },
        {
            num: '19',
            title: 'Keine Verhaltenskontrolle, keine automatisierte Entscheidung',
            body: `
        <p>Chroniq trifft keine ausschliesslich automatisierten Einzelentscheidungen einschliesslich
        Profiling, die für Mitarbeitende eine rechtliche Wirkung entfalten oder sie in vergleichbarer Weise
        erheblich beeinträchtigen (Art. 21 DSG / Art. 22 DSGVO). Über Abwesenheitsgesuche, Schichtzuteilung,
        Verwarnungen und die Beendigung eines Arbeitsverhältnisses entscheidet stets ein Mensch.</p>
        <p>Nach Art. 26 Abs. 1 der Verordnung 3 zum Arbeitsgesetz dürfen Überwachungs- und Kontrollsysteme,
        die das Verhalten der Arbeitnehmenden am Arbeitsplatz überwachen sollen, <strong>nicht</strong>
        eingesetzt werden. Chroniq ist für die Erfassung der Arbeitszeit und die Organisation der Einsätze
        bestimmt, nicht für eine Verhaltensüberwachung. Der Arbeitgeber verpflichtet sich vertraglich, die
        Auswertungsfunktionen nicht zu einer permanenten Verhaltens- oder Leistungskontrolle zu verwenden.</p>
      `,
        },
        {
            num: '20',
            title: 'Minderjährige',
            body: `
        <p>Chroniq richtet sich an Mitarbeitende im Rahmen eines Arbeitsverhältnisses. Für die Nutzung durch
        minderjährige Mitarbeitende — etwa Lernende — ist der Arbeitgeber verantwortlich; erforderlich ist
        gegebenenfalls die Zustimmung der gesetzlichen Vertretung. Die besonderen Schutzvorschriften für
        jugendliche Arbeitnehmende nach Art. 29 ff. ArG und der Jugendarbeitsschutzverordnung bleiben
        vorbehalten und sind vom Arbeitgeber einzuhalten.</p>
      `,
        },
        {
            num: '21',
            title: 'Änderungen dieser Erklärung',
            body: `
        <p>Wir überprüfen diese Erklärung regelmässig und passen sie an, wenn sich die Rechtslage oder
        unsere Bearbeitungen ändern. Die jeweils geltende Fassung ist unter
        <a href="https://www.gastro-management.ch/datenschutz#chroniq">gastro-management.ch/datenschutz</a>
        abrufbar. Über wesentliche Änderungen informieren wir registrierte Nutzerinnen und Nutzer per
        E-Mail. Das Datum der letzten Überarbeitung steht im Seitenkopf.</p>
      `,
        },
    ],
};
